import { useEffect, useState } from 'react'
import { fetchCarReport, ReportError } from './client'
import type { CarReport } from './types'
import { newViewId } from './usage'

export type CarReportState =
  | { status: 'loading' }
  | {
      status: 'loaded'
      report: CarReport
      /** The random identifier this page view's usage events are counted under. See `usage.ts`. */
      viewId?: string
      /** How long the visitor waited for the report, cold start included. */
      loadMilliseconds: number
    }
  | { status: 'failed'; error: ReportError }

/**
 * Loads the report for one plate.
 *
 * Deliberately hand-rolled rather than pulled from a data-fetching library: the app makes exactly
 * one kind of request, has no mutations and no cross-page cache to invalidate, and the API already
 * sets `Cache-Control` so the browser's own HTTP cache handles a repeat lookup. A query library
 * here would be more bundle than behaviour.
 *
 * Each load is a new page view with its own view id, so the next plate's reading time and clicks are
 * never counted against this one.
 */
export function useCarReport(plate: string): CarReportState {
  const [state, setState] = useState<CarReportState>({ status: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    const viewId = newViewId()
    const started = performance.now()
    setState({ status: 'loading' })

    fetchCarReport(plate, controller.signal, viewId)
      .then((report) =>
        setState({
          status: 'loaded',
          report,
          viewId,
          loadMilliseconds: Math.round(performance.now() - started),
        }),
      )
      .catch((error: unknown) => {
        // A navigation away from the page aborts the request; that is not a failure to report.
        if (controller.signal.aborted) {
          return
        }

        setState({
          status: 'failed',
          error:
            error instanceof ReportError
              ? error
              : new ReportError('unknown', 'Er ging iets mis bij het ophalen van dit rapport.'),
        })
      })

    return () => controller.abort()
  }, [plate])

  return state
}
